import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import HierarchyNodeBadge from './HierarchyNodeBadge';
import { formatMentorName } from './hierarchyUtils';

/**
 * Top-down org-chart rendering of the same tree data.
 *
 * Layout is pure CSS (nested flex columns); connector lines are an SVG overlay
 * drawn from measured node positions. No charting library — Chart.js is the
 * wrong tool for node-link diagrams and d3 isn't worth the dependency here.
 */

/**
 * Orthogonal parent→child connector: down, across at the midpoint, down again,
 * with the two corners rounded. Radius shrinks on short/narrow runs so the arcs
 * never overshoot the segments they join.
 */
const elbow = ({ x1, y1, x2, y2 }) => {
  const midY = (y1 + y2) / 2;
  const dx = x2 - x1;

  // A straight drop (child centred under parent) needs no corners at all.
  if (Math.abs(dx) < 1) return `M ${x1} ${y1} L ${x2} ${y2}`;

  const dir = dx > 0 ? 1 : -1;
  const r = Math.min(8, Math.abs(dx) / 2, Math.abs(midY - y1), Math.abs(y2 - midY));

  return [
    `M ${x1} ${y1}`,
    `V ${midY - r}`,
    `Q ${x1} ${midY} ${x1 + r * dir} ${midY}`,
    `H ${x2 - r * dir}`,
    `Q ${x2} ${midY} ${x2} ${midY + r}`,
    `V ${y2}`,
  ].join(' ');
};

const ChartNode = ({ node, onEdit, onFocus, collapsed, onToggle, registerRef }) => {
  const hasChildren = node.children?.length > 0;
  const isCollapsed = collapsed.has(node.id);

  return (
    <div className="hierarchy-chart-node">
      <div
        className={`hierarchy-chart-box${node.rankViolation ? ' hierarchy-chart-box-violation' : ''}`}
        ref={(el) => registerRef(node.id, el)}
      >
        <div className="hierarchy-chart-box-header">
          <span className="hierarchy-chart-name">{formatMentorName(node)}</span>
          {node.manuallyAssigned && (
            <i
              className="bi bi-lock-fill hierarchy-lock"
              title="Set manually — sync will not change this mentor's hierarchy"
            />
          )}
          {node.rankViolation && (
            <i
              className="bi bi-exclamation-triangle-fill hierarchy-violation"
              title="This mentor's current supervisor does not outrank them. Edit to fix."
            />
          )}
          {node.inCycle && (
            <i
              className="bi bi-arrow-repeat hierarchy-violation"
              title="Part of a supervision loop. Clear one of the links to break it."
            />
          )}
        </div>

        <HierarchyNodeBadge hierarchy={node.hierarchy} />

        <div className="hierarchy-chart-meta">
          {node.studentsCount || 0} student{node.studentsCount === 1 ? '' : 's'}
        </div>

        <div className="hierarchy-chart-actions">
          <button type="button" onClick={() => onEdit(node)} title="Edit hierarchy">
            <i className="bi bi-pencil" />
          </button>
          {hasChildren && (
            <>
              <button
                type="button"
                onClick={() => onFocus(node)}
                title="Focus on this subtree"
              >
                <i className="bi bi-zoom-in" />
              </button>
              <button
                type="button"
                onClick={() => onToggle(node.id)}
                title={isCollapsed ? 'Expand reports' : 'Collapse reports'}
              >
                <i className={`bi ${isCollapsed ? 'bi-plus-square' : 'bi-dash-square'}`} />
              </button>
            </>
          )}
        </div>

        {hasChildren && isCollapsed && (
          <div className="hierarchy-chart-collapsed-count">
            +{node.children.length} hidden
          </div>
        )}
      </div>

      {hasChildren && !isCollapsed && (
        <div className="hierarchy-chart-children">
          {node.children.map((child) => (
            <ChartNode
              key={child.id}
              node={child}
              onEdit={onEdit}
              onFocus={onFocus}
              collapsed={collapsed}
              onToggle={onToggle}
              registerRef={registerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const HierarchyChart = ({ tree, orphans, onEdit }) => {
  const [collapsed, setCollapsed] = useState(new Set());
  const [focusPath, setFocusPath] = useState([]); // breadcrumb of focused nodes
  const [edges, setEdges] = useState([]);
  const [orphanEdges, setOrphanEdges] = useState([]);
  // The SVG overlay is absolutely positioned inside a scroll box, so `100%`
  // would size it to the *visible* box and clip every line past the first
  // screenful. Size it to the scrollable content instead.
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [orphanSize, setOrphanSize] = useState({ width: 0, height: 0 });

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const orphanRef = useRef(null);
  const nodeRefs = useRef(new Map());

  const registerRef = useCallback((id, el) => {
    if (el) nodeRefs.current.set(id, el);
    else nodeRefs.current.delete(id);
  }, []);

  const toggle = (id) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const focused = focusPath.length > 0 ? focusPath[focusPath.length - 1] : null;
  // Memoized so `measure` below keeps a stable identity — otherwise its effect
  // re-runs on every render and re-measures in a loop.
  const roots = useMemo(() => (focused ? [focused] : tree), [focused, tree]);

  // Re-measure connector positions whenever the rendered shape can change.
  // Coordinates are relative to `container`, so each cluster (the main chart
  // and the needs-attention cluster) measures against its own scroll box.
  const measureInto = useCallback(
    (container, rootList, out) => {
      if (!container) return { width: 0, height: 0 };
      const base = container.getBoundingClientRect();

      const walk = (node) => {
        if (collapsed.has(node.id)) return;
        const parentEl = nodeRefs.current.get(node.id);
        (node.children || []).forEach((child) => {
          const childEl = nodeRefs.current.get(child.id);
          if (parentEl && childEl) {
            const p = parentEl.getBoundingClientRect();
            const c = childEl.getBoundingClientRect();
            out.push({
              key: `${node.id}-${child.id}`,
              x1: p.left + p.width / 2 - base.left + container.scrollLeft,
              y1: p.bottom - base.top + container.scrollTop,
              x2: c.left + c.width / 2 - base.left + container.scrollLeft,
              y2: c.top - base.top + container.scrollTop,
            });
          }
          walk(child);
        });
      };

      rootList.forEach(walk);

      return {
        width: Math.max(container.scrollWidth, container.clientWidth),
        height: Math.max(container.scrollHeight, container.clientHeight),
      };
    },
    [collapsed]
  );

  // Only replace size state when it actually changed — ResizeObserver fires on
  // our own updates, and an unconditional setState would loop.
  const sameSize = (a, b) => a.width === b.width && a.height === b.height;

  const measure = useCallback(() => {
    const main = [];
    const mainSize = measureInto(containerRef.current, roots, main) || { width: 0, height: 0 };
    setEdges(main);
    setCanvasSize((prev) => (sameSize(prev, mainSize) ? prev : mainSize));

    const orphanEdgeList = [];
    const oSize = (!focused && measureInto(orphanRef.current, orphans, orphanEdgeList)) || {
      width: 0,
      height: 0,
    };
    setOrphanEdges(orphanEdgeList);
    setOrphanSize((prev) => (sameSize(prev, oSize) ? prev : oSize));
  }, [measureInto, roots, orphans, focused]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    // Observe the canvas too — the scroll box's own size doesn't change when a
    // subtree expands, but the content it wraps (and every edge) does.
    const observer = new ResizeObserver(() => measure());
    if (containerRef.current) observer.observe(containerRef.current);
    if (canvasRef.current) observer.observe(canvasRef.current);
    if (orphanRef.current) observer.observe(orphanRef.current);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  return (
    <div className="hierarchy-chart-wrap">
      {focusPath.length > 0 && (
        <nav className="hierarchy-breadcrumb" aria-label="Chart focus">
          <button type="button" onClick={() => setFocusPath([])}>
            Full chart
          </button>
          {focusPath.map((node, i) => (
            <React.Fragment key={node.id}>
              <i className="bi bi-chevron-right" />
              <button
                type="button"
                onClick={() => setFocusPath(focusPath.slice(0, i + 1))}
                disabled={i === focusPath.length - 1}
              >
                {formatMentorName(node)}
              </button>
            </React.Fragment>
          ))}
        </nav>
      )}

      <div className="hierarchy-chart-scroll" ref={containerRef}>
        <svg
          className="hierarchy-chart-lines"
          aria-hidden="true"
          width={canvasSize.width}
          height={canvasSize.height}
        >
          {edges.map((e) => (
            <path key={e.key} d={elbow(e)} fill="none" />
          ))}
        </svg>

        <div className="hierarchy-chart-canvas" ref={canvasRef}>
          {roots.length === 0 && <p className="hierarchy-empty">No mentors to chart.</p>}
          {roots.map((node) => (
            <ChartNode
              key={node.id}
              node={node}
              onEdit={onEdit}
              onFocus={(n) => setFocusPath([...focusPath, n])}
              collapsed={collapsed}
              onToggle={toggle}
              registerRef={registerRef}
            />
          ))}
        </div>
      </div>

      {!focused && orphans.length > 0 && (
        <div className="hierarchy-chart-orphans">
          <h4 className="hierarchy-orphans-title">
            <i className="bi bi-exclamation-triangle" /> Needs attention ({orphans.length})
          </h4>
          {/* Rendered as full nodes, not flat boxes — a mentor caught in a
              supervision loop can still have reports, and hiding them here
              would drop them from the chart entirely. */}
          <div className="hierarchy-chart-orphan-row" ref={orphanRef}>
            <svg
              className="hierarchy-chart-lines"
              aria-hidden="true"
              width={orphanSize.width}
              height={orphanSize.height}
            >
              {orphanEdges.map((e) => (
                <path key={e.key} d={elbow(e)} fill="none" />
              ))}
            </svg>
            {orphans.map((node) => (
              <ChartNode
                key={node.id}
                node={node}
                onEdit={onEdit}
                onFocus={(n) => setFocusPath([...focusPath, n])}
                collapsed={collapsed}
                onToggle={toggle}
                registerRef={registerRef}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HierarchyChart;
